import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, Filter, Search, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatLcp } from '../formatLcp';
import MetricHelp from './MetricHelp';
import {
  METRIC_HELP,
  clsBadgeClass,
  durationBadgeClass,
  formatCls,
  formatOptionalDuration,
  scoreBadgeClass,
} from '../metrics';

const DataTable = ({ data, loading, onRefresh }) => {
  const [sortField, setSortField] = useState('measuredAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterTool, setFilterTool] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 20;

  // Get unique domains and tools for filter dropdowns
  const uniqueDomains = useMemo(() => {
    const domains = [...new Set(data.map(item => item.domain))];
    return domains.filter(domain => domain).sort();
  }, [data]);

  const uniqueTools = useMemo(() => {
    const tools = [...new Set(data.map(item => item.tool))];
    return tools.filter(tool => tool).sort();
  }, [data]);

  // Sort and filter data
  const processedData = useMemo(() => {
    let filtered = data;

    // Filter out rows with page score values of 0, 0.0, null, etc.
    filtered = filtered.filter(item => {
      const score = item.onPageScore;
      return score !== null && score !== undefined && score !== 0 && score !== 0.0 && score !== '';
    });

    // Apply domain filter
    if (filterDomain) {
      filtered = filtered.filter(item => item.domain === filterDomain);
    }

    // Apply tool filter
    if (filterTool) {
      filtered = filtered.filter(item => item.tool === filterTool);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tool.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.datum.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'createdTime' || sortField === 'measuredAt' || sortField === 'datum' || sortField === 'time') {
        aValue = new Date(a.measuredAt || a.createdTime);
        bValue = new Date(b.measuredAt || b.createdTime);
      } else if (sortField === 'onPageScore' || sortField === 'lcp' || sortField === 'inp' || sortField === 'cls' || sortField === 'ti') {
        const aNumber = typeof a[sortField] === 'number' ? a[sortField] : Number.NaN;
        const bNumber = typeof b[sortField] === 'number' ? b[sortField] : Number.NaN;
        const aMissing = !Number.isFinite(aNumber);
        const bMissing = !Number.isFinite(bNumber);

        if (aMissing || bMissing) {
          if (aMissing && bMissing) {
            return 0;
          }

          return aMissing ? 1 : -1;
        }

        aValue = aNumber;
        bValue = bNumber;
      }

      if (aValue === bValue) {
        return 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      }

      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [data, sortField, sortDirection, filterDomain, filterTool, searchTerm]);

  // Pagination logic
  const totalRecords = processedData.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = processedData.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterDomain, filterTool, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      // Adjust start if we're near the end
      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Table Header with Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <h2 className="text-lg font-semibold text-gray-900">Speed Test Data</h2>
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
              {totalRecords} records
            </span>
            {totalPages > 1 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-600 text-sm rounded-full">
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onRefresh}
              className="btn-secondary flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filterDomain}
            onChange={(e) => setFilterDomain(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Domains</option>
            {uniqueDomains.map(domain => (
              <option key={domain} value={domain}>{domain}</option>
            ))}
          </select>
          
          <select
            value={filterTool}
            onChange={(e) => setFilterTool(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Tools</option>
            {uniqueTools.map(tool => (
              <option key={tool} value={tool}>{tool}</option>
            ))}
          </select>
          
          <button
            onClick={() => {
              setFilterDomain('');
              setFilterTool('');
              setSearchTerm('');
            }}
            className="btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="table-header">
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('domain')}
                  className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                >
                  <span>Domain</span>
                  <SortIcon field="domain" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('tool')}
                  className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                >
                  <span>Tool</span>
                  <SortIcon field="tool" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('onPageScore')}
                  className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                >
                  <span>Page Score</span>
                  <SortIcon field="onPageScore" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleSort('lcp')}
                    className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                  >
                    <span>LCP</span>
                    <SortIcon field="lcp" />
                  </button>
                  <MetricHelp label="LCP" text={METRIC_HELP.lcp} />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleSort('inp')}
                    className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                  >
                    <span>INP</span>
                    <SortIcon field="inp" />
                  </button>
                  <MetricHelp label="INP" text={METRIC_HELP.inp} />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleSort('cls')}
                    className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                  >
                    <span>CLS</span>
                    <SortIcon field="cls" />
                  </button>
                  <MetricHelp label="CLS" text={METRIC_HELP.cls} />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleSort('ti')}
                    className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                  >
                    <span>TI</span>
                    <SortIcon field="ti" />
                  </button>
                  <MetricHelp label="TI" text={METRIC_HELP.ti} />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('datum')}
                  className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                >
                  <span>Date</span>
                  <SortIcon field="datum" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('time')}
                  className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
                >
                  <span>Time</span>
                  <SortIcon field="time" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentRecords.map((record) => (
              <tr key={record.id} className="table-row">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="font-medium text-gray-900">{record.domain}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    {record.tool}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${scoreBadgeClass(record.onPageScore)}`}>
                    {record.onPageScore.toFixed(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${durationBadgeClass(record.lcp, 2500, 4000)}`}>
                    {formatLcp(record.lcp)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${durationBadgeClass(record.inp, 200, 500)}`}>
                    {formatOptionalDuration(record.inp)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${clsBadgeClass(record.cls)}`}>
                    {formatCls(record.cls)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${durationBadgeClass(record.ti, 2500, 5000)}`}>
                    {formatOptionalDuration(record.ti)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {record.datum}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {record.time}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {processedData.length === 0 && (
          <div className="text-center py-12">
            <Filter className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No data found</h3>
            <p className="text-gray-500">Try adjusting your filters or search terms.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(endIndex, totalRecords)} of {totalRecords} results
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Previous button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {/* Page numbers */}
              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              {/* Next button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
